import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { RiWhatsappFill } from "react-icons/ri";
import { MdOutlineEdit, MdClose } from "react-icons/md";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';

//components
import ReactInputMask from "react-input-mask";
import Avatar from "react-avatar";
import { toast } from "react-toastify";
import { Card, Col, Form, OverlayTrigger, Row, Spinner, Tooltip } from "react-bootstrap"
import { ActionCard } from "../action-card";
import Error from "../../../../../../components/Error";

//utils
import { colors } from "../../../../../../../theme/theme";
import { saveProfileDetails } from "../../../../../../services/profile.service";

const validationSchema = Yup.object({
    name: Yup.string().trim().required("Este campo é obrigatório"),
    username: Yup.string().trim().required("Este campo é obrigatório"),
    phone: Yup.string().required("Este campo é obrigatório"),
    is_whatsapp: Yup.boolean().required("Este campo é obrigatório"),
}).required();

export const CardAccountDetail = (props: any): JSX.Element => {
    const { user, setUser, updateContext, userContext } = props;
    const { t: translation } = useTranslation();    
    const { register, handleSubmit, control, formState: { errors } } = useForm({ resolver: yupResolver(validationSchema) });

    const [ loading, setLoading] = useState(false);
    const [ updateAccount, setUpdateAccount] = useState(false);
    const [ removePhotoUser, setRemovePhotoUser] = useState(false);
    const [ originalPreviewPhoto, setOriginalPreviewPhoto] = useState(null);
    const [ previewPhoto, setPreviewPhoto] = useState("");

    const handleChangePhoto = (img: any) => {
        setOriginalPreviewPhoto(img);
        setPreviewPhoto(URL.createObjectURL(img));
        setRemovePhotoUser(true);
    }

    const onSubmit = (params: any) => {
        setLoading(true)

        if (removePhotoUser) {
            params['picture'] = "";
        } else if (originalPreviewPhoto === null) {
            params['picture'] = user.picture;
        } else {
            params['picture'] = originalPreviewPhoto
        }

        saveProfileDetails(user.id, params).then((response: any) => {
            setUser(response);
            updateContext({ ...userContext, picture: response.picture, name: response.name, phone: response.phone, username: response.username});
            setUpdateAccount(false);
            setOriginalPreviewPhoto(null);
            setPreviewPhoto("");
            toast.success("Dados da conta alterados com sucesso!");
            setLoading(false)
        }).catch((err) => {
            toast.error(err.response.data[0]);
            setLoading(false)
        })
    }

    useEffect(() => {
        if(previewPhoto !== "" && removePhotoUser) {
            setRemovePhotoUser(false);
        }
    },[previewPhoto])

    return (
        <Card className="account-card mb-4">
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Card.Body>
                    <Card.Title className="ml-5 mr-5 mb-0">
                        <Row>
                            <Col sm={6} className="d-flex align-items-center">
                                <h4 className="mb-0" style={{ fontSize: '22px' }}>{translation('userAccount.account')}</h4>
                            </Col>
                            <Col sm={6} className="text-end">
                                {
                                    loading ? (
                                        <Spinner animation="border" variant="primary" style={{ width: '2rem', height: '2rem', borderWidth: '0.25em', borderRightColor: 'transparent' }}/>
                                    ) : (
                                        <ActionCard message={translation('userAccount.updateAccount')} show={updateAccount} setChange={setUpdateAccount}></ActionCard>
                                    )
                                }
                            </Col>
                        </Row>
                    </Card.Title>
                    <hr />
                    <Row>
                        <Col md={3} className={`d-flex justify-content-center align-items-center mb-3 ${updateAccount ? 'photo-edit-group' : ''} `}>
                            {
                                updateAccount && 
                                    <>
                                        <Form.Group className="mb-2">
                                            <OverlayTrigger
                                                placement={'top'}
                                                overlay={<Tooltip id="edit-photo">{user.picture ? (removePhotoUser ? "Adicionar Foto" : 'Editar Foto') : "Adicionar Foto"}</Tooltip>}
                                            >
                                                <Form.Label htmlFor="photo_edit" className="text-darker-grey"><MdOutlineEdit/></Form.Label>
                                            </OverlayTrigger>
                                            <Form.Control type="file" id="photo_edit" onChange={(e: any) => handleChangePhoto(e.target.files[0])}/>
                                        </Form.Group>
                                        
                                        <OverlayTrigger
                                            placement={'top'}
                                            overlay={<Tooltip id="remove-photo">Remover Foto</Tooltip>}
                                        >
                                            <Form.Label htmlFor="photo_remove" className="text-darker-grey" style={{ left: '14%'}} onClick={() => {
                                                setRemovePhotoUser(true);
                                                setOriginalPreviewPhoto(null);
                                                setPreviewPhoto("");
                                            } }>
                                                <MdClose/>
                                            </Form.Label>
                                        </OverlayTrigger>
                                    </>
                                }
                            <Avatar 
                                size="150" round={true} name={user.username !== "" ? user.username : "U S"} 
                                src={
                                    !removePhotoUser ? (
                                        previewPhoto !== "" ? previewPhoto : (user.picture ?? "")
                                    ) : ""
                                } color={colors.mainColors.blueberry} 
                            />
                        </Col>
                        <Col md={9} lg={5} className="d-flex align-items-center">
                            <Row>
                                <Col xs={12} className="mb-2">
                                    {
                                        updateAccount ? (
                                            <Form.Group className="mb-2">
                                                <Form.Label className="text-darker-grey">{translation('userAccount.displayName')}:</Form.Label><br />
                                                <Form.Control type="text" placeholder={translation('userAccount.displayName')} defaultValue={ user.name } {...register("name", { required: true })} />
                                                {errors.name ? (<div className="w-100"> <Error>{errors.name?.message}</Error> </div>) : ""}
                                            </Form.Group>
                                        ) : (
                                            <h6><span className="text-darker-grey">{translation('userAccount.displayName')}:</span> {user.name}</h6>
                                        )
                                    }
                                </Col>
                                <Col xs={12} className="mb-2">
                                    {
                                        updateAccount ? (
                                            <Row>
                                                <Col xs={12} md={7}>
                                                    <Controller
                                                        name="phone"
                                                        control={control}
                                                        rules={{ required: true }}
                                                        defaultValue={ user.phone }
                                                        render={({ field }) => (
                                                            <ReactInputMask mask="(99) 99999-9999"
                                                                value={field.value}
                                                                onChange={field.onChange}
                                                            >
                                                                {(inputProps: any) => (
                                                                    <Form.Group className="mb-2">
                                                                        <Form.Label className="text-darker-grey">{translation('userAccount.phone')}:</Form.Label><br />
                                                                        <Form.Control type="text" {...inputProps} />
                                                                        {errors.phone ? (<div className="w-100"> <Error>{errors.phone?.message}</Error> </div>) : ""}
                                                                    </Form.Group>
                                                                )}
                                                            </ReactInputMask>
                                                        )}
                                                    />
                                                </Col>
                                                <Col className="d-flex align-items-end pb-3">
                                                    <Form.Check
                                                        type="switch"
                                                        data-testid="wpp-switch"
                                                        label="É WhatsApp"
                                                        defaultChecked={user?.is_whatsapp}
                                                        {...register("is_whatsapp")}
                                                    />
                                                </Col>
                                            </Row>
                                        ) : (
                                            <h6>
                                                <span className="text-darker-grey">{translation('userAccount.phone')}:</span> { user.phone }
                                                {
                                                    user.is_whatsapp &&
                                                    <RiWhatsappFill style={{ width: "20px", height: "20px", marginLeft: "5px", paddingBottom: '3px', color: "#25ca66" }} />
                                                }
                                            </h6>
                                        )
                                    }
                                </Col>
                                <Col xs={12} className="mb-2">
                                    {
                                        updateAccount ? (
                                            <Form.Group className="mb-2">
                                                <Form.Label className="text-darker-grey">{translation('userAccount.responsible')}:</Form.Label>
                                                <Form.Control type="text" placeholder="Nome de exibição" defaultValue={user.username} {...register("username", { required: true })} />
                                                {errors.username ? (<div className="w-100"> <Error>{errors.username?.message}</Error> </div>) : ""}
                                            </Form.Group>
                                        ) : (
                                            <h6><span className="text-darker-grey">{translation('userAccount.responsible')}:</span> { user.username }</h6>
                                        )
                                    }
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Card.Body>
            </Form>
        </Card>
    )
}